/**
 * Phone chrome — a top bar and the overlay that holds the rest of the nav.
 *
 * The docked sidebar is a desktop tool. On a phone it would steal a third of
 * the width from the recipe grid, so it hides below `md` and these two pieces
 * replace it. Destinations live in the hamburger overlay, next to theme and
 * account; the bottom of the screen is left for the recipe vitals pin when
 * one is open. The top bar carries the same pint + wordmark and assistant
 * orb as the sidebar, so the product still looks like Beerwright when the
 * column is gone, plus the sync status: "is my work safe?" and the tap that
 * answers it must not be behind a navigation menu.
 */

import { srmToHex } from '@beerwright/core';
import { useRouterState } from '@tanstack/react-router';
import { MenuIcon, SearchIcon, XIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useAssistant } from '@/ai/assistant-context';
import { AppSidebar } from '@/components/app-sidebar';
import { AssistantOrb } from '@/components/assistant-orb';
import { openCommandPalette } from '@/components/command-palette';
import { BeerGlass } from '@/components/recipe/beer-glass';
import { SyncStatusIndicator } from '@/components/sync-status';
import { Button } from '@/components/ui/button';

export interface MobileNavProps {
  readonly menuOpen: boolean;
  readonly onMenuOpenChange: (open: boolean) => void;
}

export function MobileTopBar({ menuOpen, onMenuOpenChange }: MobileNavProps) {
  const assistant = useAssistant();

  return (
    <header
      className="flex shrink-0 items-center gap-2 border-b border-border bg-background px-2 md:hidden"
      style={{
        paddingTop: 'var(--safe-top)',
        // 3.5rem, not 3: this bar exists for one thumb reaching for one button,
        // and the 44px floor every platform guideline names is a floor for the
        // *target*, not the bar — the bar has to be taller than the target it
        // holds. The buttons below override the default icon size (36px) up to
        // that floor for the same reason.
        minHeight: 'calc(3.5rem + var(--safe-top))',
      }}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-11"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        onClick={() => onMenuOpenChange(!menuOpen)}
      >
        {menuOpen ? <XIcon /> : <MenuIcon />}
      </Button>
      {/* The logo opens the menu too. It was inert — not even a link home —
          which made the largest, most obviously tappable thing in the bar the
          one thing that did nothing. Same handler as the button beside it, so
          there is one behaviour to reason about rather than two. */}
      <button
        type="button"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        className="flex min-w-0 cursor-pointer items-center gap-2 rounded-md bg-transparent text-left"
        onClick={() => onMenuOpenChange(!menuOpen)}
      >
        <BeerGlass color={srmToHex(8)} logo className="h-7 w-9 shrink-0" />
        <span data-slot="wordmark" className="min-w-0 truncate">
          Beerwright
        </span>
      </button>
      {/* `ml-auto` on the group rather than on a button, so the right edge
          holds whether or not the search button renders. */}
      <div className="ml-auto flex items-center gap-2">
        {/* The sync status, for the same reason search is here and by the same
            argument the indicator was given a button with in the first place
            (PLAN-SYNC-LIVENESS §4, PLAN-UX-PASS F5). Its only mount was the
            sidebar, which on a phone is the drawer behind the hamburger — so
            "Syncing · 7", the number a brewer can read us over a stuck queue,
            and `Reload needed`, the only in-app way out of a tab the sync
            protocol has moved past, both sat behind a navigation menu. A shell
            with no pull-to-refresh cannot also hide its reload.

            Collapsed, because a bar that already carries a wordmark has room
            for a dot and not for a sentence; the drawer's row keeps the words.
            The wrapper is the bar's own 44px target, and stretches the
            indicator's 28px control to fill it. */}
        <div className="flex size-11 shrink-0 items-center justify-center [&>*]:h-full">
          <SyncStatusIndicator collapsed />
        </div>
        {/* Search sits here rather than in the drawer, where it read as a
            desktop control that had wandered in: a row styled like the docked
            sidebar's, above a list of destinations, behind a menu you had to
            open first. The palette is how a phone reaches jump-to-recipe,
            undo, redo and the theme switch, so it wants a thumb's reach rather
            than a second tap.
            No pointer test, unlike the sidebar's copy. That one is persistent
            desktop chrome, where a mouse certainly has ⌘K and a second door is
            clutter. This bar only exists below `md` at all, so the width has
            already chosen the audience; testing the pointer on top of it would
            only hide the one way in from any device that reports a fine
            pointer without a keyboard behind it. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Search and commands"
          className="size-11"
          onClick={openCommandPalette}
        >
          <SearchIcon className="size-5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={assistant.open ? 'Close the assistant' : 'Open the assistant'}
          aria-pressed={assistant.open}
          className="size-11"
          onClick={() => assistant.toggle()}
        >
          <AssistantOrb className="size-5" />
        </Button>
      </div>
    </header>
  );
}

export function MobileNavDrawer({ menuOpen, onMenuOpenChange }: MobileNavProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Pathname is the trigger, not a value the effect reads: leaving a page
  // should dismiss the overlay even when the closer callback is stable.
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on route change
  useEffect(() => {
    onMenuOpenChange(false);
  }, [pathname, onMenuOpenChange]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onMenuOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, onMenuOpenChange]);

  if (!menuOpen) return null;

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <button
        type="button"
        aria-label="Close menu"
        className="absolute inset-0 bg-black/50"
        onClick={() => onMenuOpenChange(false)}
      />
      <div className="absolute inset-y-0 left-0 flex max-w-[85vw] shadow-xl">
        <AppSidebar
          collapsed={false}
          onToggleCollapsed={() => onMenuOpenChange(false)}
          variant="drawer"
        />
      </div>
    </div>
  );
}
